import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, FileText } from 'lucide-react';
import api from '../utils/api';

const POLICY_TYPES = [
  { type: 'terms_conditions', title: 'Terms & Conditions' },
  { type: 'privacy_policy', title: 'Privacy Policy' },
  { type: 'refund_policy', title: 'Refund & Cancellation Policy' },
  { type: 'return_policy', title: 'Return Policy' },
  { type: 'shipping_policy', title: 'Shipping Policy' }
];

export default function PolicyPage() {
  const { type } = useParams();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPolicies, setAllPolicies] = useState([]);

  const fetchPolicy = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/policies/${type || 'terms_conditions'}`);
      setPolicy(response.data);
    } catch (error) {
      setPolicy(null);
    } finally {
      setLoading(false);
    }
  }, [type]);

  const fetchAllPolicies = useCallback(async () => {
    try {
      const response = await api.get('/policies');
      setAllPolicies(response.data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchPolicy();
    fetchAllPolicies();
  }, [fetchPolicy, fetchAllPolicies]);

  const currentType = type || 'terms_conditions';

  if (loading) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a3a5c]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-12 bg-gray-50" data-testid="policy-page">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-[#1a3a5c]">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-[#1a3a5c]">{policy?.title || 'Policies'}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Policy Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sticky top-24">
              <h3 className="font-serif text-lg font-semibold text-[#1a3a5c] mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Legal Policies
              </h3>
              <nav className="space-y-1">
                {POLICY_TYPES.map((p) => (
                  <Link
                    key={p.type}
                    to={`/policies/${p.type}`}
                    className={`block px-3 py-2 rounded-md text-sm transition-colors ${
                      currentType === p.type
                        ? 'bg-[#1a3a5c] text-white'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-[#1a3a5c]'
                    }`}
                    data-testid={`policy-nav-${p.type}`}
                  >
                    {p.title}
                  </Link>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              key={currentType}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8"
            >
              {policy ? (
                <>
                  <h1 className="font-serif text-2xl md:text-3xl font-bold text-[#1a3a5c] mb-6 pb-4 border-b border-gray-200">
                    {policy.title}
                  </h1>
                  
                  <div className="space-y-8">
                    {policy.sections && policy.sections.length > 0 ? (
                      policy.sections
                        .filter(section => section.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map((section, index) => (
                          <div key={section.id || index} className="policy-section">
                            {section.title && (
                              <h2 className="font-serif text-xl font-semibold text-[#1a3a5c] mb-3">
                                {section.title}
                              </h2>
                            )}
                            <div 
                              className="prose prose-sm max-w-none text-gray-600 leading-relaxed policy-content"
                              dangerouslySetInnerHTML={{ __html: section.content }}
                            />
                          </div>
                        ))
                    ) : (
                      <p className="text-gray-500">No content available for this policy.</p>
                    )}
                  </div>

                  {policy.updated_at && (
                    <div className="mt-8 pt-4 border-t border-gray-200 text-sm text-gray-500">
                      Last updated: {new Date(policy.updated_at).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-600 mb-2">Policy Not Found</h2>
                  <p className="text-gray-500">This policy document is not available yet.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .policy-content p {
          margin-bottom: 1rem;
        }
        .policy-content ul, .policy-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .policy-content li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
